import type { HTMLAttributes, ReactNode } from 'react';
import { classNames } from './classNames';

export const habitaSurfaceClass = 'rounded-lg border border-border-subtle bg-card';
export const habitaControlBorderClass = 'border-border-subtle';
export const habitaDividerClass = 'divide-y divide-border-subtle';

interface HabitaSurfaceProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

export function HabitaSurface({ children, className, ...props }: HabitaSurfaceProps) {
  return (
    <section {...props} className={classNames(habitaSurfaceClass, className)}>
      {children}
    </section>
  );
}

export function HabitaSectionHeader({
  title,
  description,
  icon,
  iconClassName,
  titleClassName,
  children,
  className,
}: {
  title: string;
  description?: ReactNode;
  icon?: string;
  iconClassName?: string;
  titleClassName?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={classNames('border-b border-border-subtle px-5 py-4', className)}>
      <div className={classNames('flex justify-between gap-3', description ? 'items-start' : 'items-center')}>
        <div className="min-w-0">
          <h2 className={classNames('flex items-center gap-2 font-fraunces text-sm font-semibold text-textPrimary', titleClassName)}>
            {icon && <i className={classNames(`${icon} text-base text-textSecondary`, iconClassName)} aria-hidden />}
            {title}
          </h2>
          {description && (
            <p className="mt-1 font-instrument text-xs font-normal text-textSecondary">
              {description}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

export function HabitaEmptyState({
  icon,
  title,
  description,
  className,
}: {
  icon: string;
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <HabitaSurface className={classNames('px-6 py-12 text-center', className)}>
      <i className={`${icon} text-4xl text-textSecondary/60`} aria-hidden />
      <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">{title}</p>
      <p className="mt-1 font-instrument text-sm text-textSecondary">{description}</p>
    </HabitaSurface>
  );
}

export function HabitaErrorNotice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-red-300/40 bg-red-500/10 px-4 py-3 font-instrument text-sm text-red-700 dark:text-red-200">
      {children}
    </div>
  );
}
